const preloadErrorReload = (retries = 1) => {
  const errRetries: string | null = sessionStorage.getItem("errRetries");
  if (typeof errRetries === "string") {
    if (parseInt(errRetries) >= retries) {
      return;
    }
    sessionStorage.setItem("errRetries", String(parseInt(errRetries) + 1));
  } else {
    sessionStorage.setItem("errRetries", "1");
  }
  console.debug("Reloaded page after preload error", retries);
  window.location.reload();
};
window.addEventListener("vite:preloadError", event => {
  preloadErrorReload(3);
  event.preventDefault();
});

import { createApp, ref, readonly, onBeforeUnmount } from "vue";
import App from "./App.vue";
import { VueHeadMixin } from "@unhead/vue";
import { createHead } from "@unhead/vue/client";
import { TemplateParamsPlugin } from "@unhead/vue/plugins";
import { setDefaultProps } from "vue-tippy";
import { router } from "@/router";
import { setupApp } from "composables/useApp";
import { LD_READY, LD_CLIENT, LD_FLAG } from "launchdarkly-vue-client-sdk";
import { identifyLaunchDarklyUser } from "./utils/launchDarkly/ldHelper";

import resizeListenerDirective from "directives/resize-listener";

import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";

import "vue-cal/dist/vuecal.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";

// Import styles
import "@/assets/fonts/fonts.css";
import "@/assets/styles/theme.scss";

import { defineRule } from "vee-validate";
import { email as veeValidateEmailRule } from "@vee-validate/rules";

import { tasksHub, encountersHub } from "./signalr";
import { createPinia } from "pinia";
import { PiniaSharedState } from "pinia-shared-state";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import {
  localization,
  localizationDateFormat,
  localizationDateTimeFormat,
  localizationDateWithDashes,
  localizationDateWithSlashes,
  localizationRouteComponent,
  currency,
  currencySymbol,
  localizedNumber,
  helloEmail
} from "./globals";

import { useRootStore } from "./store/root";
import { RouteNames } from "./utils/constants";
import { isNavigationFailure, NavigationFailureType } from "vue-router";
import ldClient from "./utils/launchDarkly";
import { getActiveUserManager } from "./store/userManagers";
import { startTabTracking } from "@/helpers/tabTracking";

defineRule("required", (value: any, _, ctx) => {
  if (!value || !value.length) {
    return `The ${ctx.label} field is required.`;
  }
  return true;
});

defineRule("email", (value: string, _, ctx) => {
  if (!value || !value.length) {
    return true;
  }

  if (!veeValidateEmailRule(value)) {
    return `The ${ctx.label} field is invalid.`;
  }
  return true;
});

defineRule("maxLength", (value: string, [limit]: number[], ctx) => {
  // The field is empty so it should pass
  if (!value || !value.length) {
    return true;
  }
  if (value.length > limit) {
    return `The ${ctx.label} field must be less than ${limit} characters.`;
  }
  return true;
});

defineRule("minLength", (value: string, [limit]: number[], ctx) => {
  // The field is empty so it should pass
  if (!value || !value.length) {
    return true;
  }
  if (value.length < limit) {
    return `The ${ctx.label} field must be greater than ${limit - 1} characters.`;
  }
  return true;
});

defineRule("regex", (value: string, [regex]: RegExp[], ctx) => {
  // The field is empty so it should pass
  if (!value || !value.length) {
    return true;
  }

  if (!regex.test(value)) {
    return `This ${ctx.label} field is invalid.`;
  }
  return true;
});

// To support things without access to the store logging us out
// add an event listener for the logout event.
// This probably isn't ideal but it allows us to easily logout everywhere
window.addEventListener("logout", () => useRootStore().logout());
window.addEventListener("loggedOut", async () => {
  if (router.currentRoute.value.name !== RouteNames.SignIn) {
    try {
      await router.push({ name: RouteNames.SignIn });
    } catch (err) {
      // vue-router throws an error in any case where a middleware function
      // redirects the request, even if it is the expected behaviour. Catching this here
      // because sometimes there needs to be multiple attempts to redirect to properly handle multiple tabs
      if (
        !isNavigationFailure(err, NavigationFailureType.duplicated) &&
        !isNavigationFailure(err, NavigationFailureType.cancelled)
      ) {
        throw err;
      }
    }
  }
});

const head = createHead({ plugins: [TemplateParamsPlugin] });

setDefaultProps({
  arrow: false,
  animation: "shift-away"
});

const sentryDisabled = import.meta.env.VITE_APP_DISABLE_SENTRY === "true";

const pinia = createPinia();
if (!sentryDisabled) {
  pinia.use(createSentryPiniaPlugin());
}
pinia.use(piniaPluginPersistedstate);
pinia.use(
  PiniaSharedState({
    enable: false,
    initialize: true
  })
);

// Helper to sanitize sensitive data from URLs for Sentry
function sanitizeSentryUrl(urlString: string): string {
  try {
    const url = new URL(urlString, window.location.origin);

    // Always filter credential from all paths (hash and query params)
    if (url.searchParams.has("credential")) {
      url.searchParams.set("credential", "ELIDED");
    }

    // Filter credential from hash fragments
    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.substring(1));
      if (hashParams.has("credential")) {
        hashParams.set("credential", "ELIDED");
      }
      url.hash = hashParams.toString() ? "#" + hashParams.toString() : "";
    }

    // Filter payment intents
    if (url.searchParams.has("payment_intent")) {
      url.searchParams.set("payment_intent", "ELIDED");
    }
    if (url.searchParams.has("payment_intent_client_secret")) {
      url.searchParams.set("payment_intent_client_secret", "ELIDED");
    }

    // Filter sensitive data from specific paths
    if (url.pathname.includes("/forgot-password/change")) {
      if (url.searchParams.has("code")) {
        url.searchParams.set("code", "ELIDED");
      }
      if (url.searchParams.has("email")) {
        url.searchParams.set("email", "ELIDED");
      }
    }

    if (
      url.pathname.includes("/sign-up/claim-offer") ||
      url.pathname.includes("/sign-up/claim-offer-sponsored") ||
      url.pathname.includes("/sign-up/referral")
    ) {
      if (url.searchParams.has("code")) {
        url.searchParams.set("code", "ELIDED");
      }
    }

    // Return sanitized URL (pathname + search + hash)
    return url.pathname + url.search + url.hash;
  } catch {
    // If URL parsing fails, return original string
    return urlString;
  }
}

// Block first paint until LaunchDarkly is ready (up to 5s). Some flags are read imperatively at boot
// — notably the Stripe account selection in useStripe.ts (ldClient.variation), which must agree with
// the backend's NetNewChargeAccount or payment confirmation 404s on a cross-account intent. Reading a
// flag before the client initializes returns the hard-coded fallback and silently diverges from the
// backend, so we wait here rather than render immediately.
async function initializeApp() {
  // Before the LaunchDarkly wait, so a tab that closes while this one boots still counts it.
  // A failure must not stop the app mounting; it's reported once Sentry is initialized.
  let tabTrackingError: unknown;
  try {
    startTabTracking();
  } catch (error) {
    tabTrackingError = error;
    console.error("Failed to start tab tracking:", error);
  }
  let ldReady = false;
  try {
    // Wait for LaunchDarkly to be ready
    await ldClient.waitForInitialization(5);
    ldReady = true;
  } catch (error) {
    console.error("Failed to initialize LaunchDarkly:", error);
  }

  const app = createApp(App);
  if (!sentryDisabled) {
    Sentry.init({
      app,
      // Release supplied by the deploy pipeline (baked in at docker build time via
      // the VITE_SENTRY_RELEASE build arg). Matches the backend SENTRY_RELEASE and
      // the release the @sentry/vite-plugin uploads source maps under, so events +
      // maps group under one release. Undefined locally -> Sentry auto-detects.
      release: import.meta.env.VITE_SENTRY_RELEASE,
      dsn: "https://dfd2428f9e0d58df5404bed162a34002@o4509396881244160.ingest.us.sentry.io/4509010999246848",
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          blockAllMedia: false
        })
      ],
      // Tracing
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        "localhost",
        import.meta.env.VITE_APP_PLATFORM_URL,
        import.meta.env.VITE_APP_TASKNOTES_URL,
        import.meta.env.VITE_APP_INDIVIDUALS_URL,
        import.meta.env.VITE_APP_ENCOUNTERS_URL,
        import.meta.env.VITE_OBO_AUTH_URL,
        import.meta.env.VITE_APP_VIEWER_URL,
        import.meta.env.VITE_APP_DIAGNOSTICS_URL
      ],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      environment: import.meta.url.includes("localhost")
        ? "local"
        : import.meta.env.MODE.toLowerCase()
            .replace("production", "prod")
            .replace("development", "dev")
            .replace("staging", "stage")
            .replace("-uk", ""),
      beforeBreadcrumb: breadcrumb => {
        // Remove sensitive data from breadcrumbs
        if (breadcrumb.data?.to) {
          breadcrumb.data.to = sanitizeSentryUrl(breadcrumb.data.to);
        }
        if (breadcrumb.data?.from) {
          breadcrumb.data.from = sanitizeSentryUrl(breadcrumb.data.from);
        }
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = sanitizeSentryUrl(breadcrumb.data.url);
        }
        return breadcrumb;
      },
      beforeSendTransaction: transaction => {
        // Don't send sensitive data to Sentry, note it seems impossible to fully filter out all sensitive data so don't send certain paths
        if (
          transaction.request?.url?.includes("/forgot-password/change") ||
          transaction.request?.url?.includes("/sign-in/callback/function") ||
          transaction.request?.url?.includes("/sign-in/callback/login/obo") ||
          transaction.request?.url?.includes("/sign-in/callback") ||
          transaction.request?.url?.includes("/sign-up/claim-offer") ||
          transaction.request?.url?.includes(
            "/sign-up/claim-offer-sponsored"
          ) ||
          transaction.request?.url?.includes("/sign-up/referral") ||
          transaction.request?.url?.includes("/sign-in")
        ) {
          return null;
        }
        return transaction;
      }
    });
  }
  if (tabTrackingError !== undefined) Sentry.captureException(tabTrackingError);

  app
    .use(router)
    .mixin(VueHeadMixin)
    .use(head)
    .use(pinia)
    .use(tasksHub)
    .use(encountersHub)
    .directive("resize", resizeListenerDirective);

  // Provide the early LD client directly instead of using LDPlugin, which creates
  // its own internal client. This ensures identify() calls (after login) and
  // useLDFlag() reads share the same client instance for per-user targeting.
  const ldReadyRef = ref(ldReady);
  if (!ldReady) {
    ldClient.on("ready", () => (ldReadyRef.value = true));
  }
  app.provide(LD_READY, readonly(ldReadyRef));
  app.provide(LD_CLIENT, ldClient);
  app.provide(LD_FLAG, <T>(flagKey: string, defaultFlagValue?: T) => {
    const flagValue = ldReadyRef.value
      ? ldClient.variation(flagKey, defaultFlagValue)
      : defaultFlagValue;
    const flagRef = ref(flagValue);
    const update = (v: unknown) => (flagRef.value = v);
    ldClient.on(`change:${flagKey}`, update);
    onBeforeUnmount(() => ldClient.off(`change:${flagKey}`, update));
    if (!ldReadyRef.value) {
      ldClient.on(
        "ready",
        () => (flagRef.value = ldClient.variation(flagKey, flagRef.value))
      );
    }
    return readonly(flagRef);
  });

  // Re-identify if user already has a valid session (e.g., new tab with persisted auth)
  getActiveUserManager()
    .then(um => um?.getUser())
    .then(user => {
      if (user && !user.expired) {
        identifyLaunchDarklyUser(
          context => ldClient.identify(context),
          user.profile
        );
      }
    })
    .catch(e => console.debug("Session re-identify skipped", e));

  app.config.globalProperties.$localization = localization;
  app.config.globalProperties.$localizationDateFormat = localizationDateFormat;
  app.config.globalProperties.$localizationDateTimeFormat =
    localizationDateTimeFormat;
  app.config.globalProperties.$localizationDateWithDashes =
    localizationDateWithDashes;
  app.config.globalProperties.$localizationDateWithSlashes =
    localizationDateWithSlashes;
  app.config.globalProperties.$currency = currency;
  app.config.globalProperties.$currencySymbol = currencySymbol;
  app.config.globalProperties.$helloEmail = helloEmail;
  app.config.globalProperties.$localizedNumber = localizedNumber;
  app.config.globalProperties.$localizationRouteComponent =
    localizationRouteComponent;

  app.mount("#app");

  setupApp(app);
}

// Initialize the app
initializeApp();
